import {
    INBOX_PAGE_FETCH_REQUESTS,
    INBOX_PAGE_FETCH_REQUESTS_DONE,
    INBOX_PAGE_INIT,
    INBOX_PAGE_RESET,
    INBOX_PAGE_SET_DATA_PRIVACY_DIALOG_OPEN,
    INBOX_PAGE_SET_JOIN_GROUP_CONSENT_DIALOG_OPEN,
    INBOX_PAGE_SET_MUTUAL_CONNECTION_DIALOG_OPEN,
    INBOX_PAGE_SET_CHALLENGE_DIALOG_OPEN,
    INBOX_PAGE_SET_REQ_STATUS,
    INBOX_PAGE_SET_GROUP_ERROR_DIALOG_OPEN,
    INBOX_PAGE_SET_DELETE_GROUP_REQ_DIALOG_OPEN
} from '../../actions/ActionTypes';

const INITIAL_STATE = {
    viewerSocialProfile: null,
    viewerUserPreferences: null,
    requests: null,
    profileInfos: {},
    nextCursor: null,
    error: false,
    fetching: true,
    /** fallback state for challenge dialog */
    useFallback: true,
    challengeDialog: {
        open: false,
        dialogType: null,
        uniqueKey: null,
        request: null
    },
    requestsStatus: {},
    mutualConnectionDialog: {
        open: false,
        displayName: null,
        mutualConnectionsCount: null
    },
    joinGroupConsentDialog: {
        open: false,
        uniqueKey: null,
        request: null
    },
    dataPrivacyDialog: {
        open: false,
        uniqueKey: null,
        request: null
    },
    groupErrorDialog: {
        open: false,
        errorType: null
    },
    deleteGroupRequestDialog: {
        open: false,
        uniqueKey: null,
        request: null
    }
};

/** selectors */
export const selectInboxPage = (state) => state.inboxPage || INITIAL_STATE;

export const selectViewerSocialProfile = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.viewerSocialProfile;
};

export const selectViewerUserPreferences = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.viewerUserPreferences;
};

export const selectRequests = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.requests;
};

export const selectNextCursor = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.nextCursor;
};

export const selectFetching = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.fetching;
};

/** fallback state for challenge dialog (privacy) */
export const selectUseFallback = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.useFallback;
};

export const selectChallengeDialogState = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.challengeDialog;
};

export const selectDeleteGroupRequestDialogState = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.deleteGroupRequestDialog;
};

const REQUEST_STATUS = {
    UNDECIDED: 0,
    PROCESSING: 1,
    ACCEPTED: 2,
    DECLINED: 3
};

export const selectRequestStatus = (state, uniqueKey) => {
    const inboxPage = selectInboxPage(state);
    if (!inboxPage) {
        return REQUEST_STATUS.UNDECIDED;
    }
    return inboxPage.requestsStatus[uniqueKey] || REQUEST_STATUS.UNDECIDED;
};

export const selectMutualConnectionDialogState = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.mutualConnectionDialog;
};

export const selectJoinGroupConsentDialogState = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.joinGroupConsentDialog;
};
export const selectGroupErrorDialogState = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.groupErrorDialog;
};

export const selectDataPrivacyDialogState = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.dataPrivacyDialog;
};

export const selectInboxPageProfileInfosState = (state) => {
    const inboxPage = selectInboxPage(state);
    return inboxPage.profileInfos;
};

export const selectInboxPageProfileImageAndFrameOfUserId = (state, userId) => {
    const profileInfos = selectInboxPageProfileInfosState(state);
    return profileInfos?.[userId];
};

/** handlers */
const inboxPageInit = (_, action) => {
    return {
        ...INITIAL_STATE,
        viewerSocialProfile: action.viewerSocialProfile,
        viewerUserPreferences: action.viewerUserPreferences
    };
};

const inboxPageReset = () => {
    return INITIAL_STATE;
};

const inboxPageFetchRequests = (state, action) => {
    return {
        ...state,
        error: false,
        fetching: true,
        requests: action.cursor ? state.requests : null
    };
};

const inboxPageFetchRequestsDone = (state, action) => {
    if (action.error) {
        return {
            ...state,
            fetching: false,
            error: true
        };
    }
    const newRequests = (state.requests || []).concat(action.requests);
    return {
        ...state,
        error: false,
        fetching: false,
        requests: newRequests,
        nextCursor: action.nextCursor,
        profileInfos: {
            ...state.profileInfos,
            ...action.profileInfos
        }
    };
};

const inboxPageSetChallengeDialogOpen = (state, action) => {
    return {
        ...state,
        challengeDialog: {
            open: action.open,
            dialogType: action.open ? action.dialogType : state.challengeDialog.dialogType,
            uniqueKey: action.uniqueKey,
            request: action.request
        }
    };
};

const inboxPageSetRequestStatus = (state, action) => {
    return {
        ...state,
        requestsStatus: {
            ...state.requestsStatus,
            [action.uniqueKey]: action.status
        }
    };
};

const inboxPageSetMutualConnectionDialogOpen = (state, action) => {
    return {
        ...state,
        mutualConnectionDialog: {
            open: action.open,
            displayName: action.displayName || null,
            mutualConnectionsCount: action.mutualConnectionsCount || null
        }
    };
};

const inboxPageSetJoinGroupConsentDialogOpen = (state, action) => {
    return {
        ...state,
        joinGroupConsentDialog: {
            open: action.open,
            uniqueKey: action.uniqueKey || null,
            request: action.request || null
        }
    };
};

const inboxPageSetDataPrivacyDialogOpen = (state, action) => {
    return {
        ...state,
        dataPrivacyDialog: {
            open: action.open,
            uniqueKey: action.uniqueKey || null,
            request: action.request || null
        }
    };
};

const inboxPageSetGroupErrorDialogOpen = (state, { open, errorType }) => {
    return {
        ...state,
        groupErrorDialog: {
            open,
            errorType
        }
    };
};

const inboxPageSetDeleteGroupRequestDialogOpen = (state, action) => {
    return {
        ...state,
        deleteGroupRequestDialog: {
            open: action.open,
            uniqueKey: action.uniqueKey,
            request: action.request
        }
    };
};

const handlers = {
    [INBOX_PAGE_INIT]: inboxPageInit,
    [INBOX_PAGE_RESET]: inboxPageReset,
    [INBOX_PAGE_FETCH_REQUESTS]: inboxPageFetchRequests,
    [INBOX_PAGE_FETCH_REQUESTS_DONE]: inboxPageFetchRequestsDone,
    [INBOX_PAGE_SET_CHALLENGE_DIALOG_OPEN]: inboxPageSetChallengeDialogOpen,
    [INBOX_PAGE_SET_REQ_STATUS]: inboxPageSetRequestStatus,
    [INBOX_PAGE_SET_MUTUAL_CONNECTION_DIALOG_OPEN]: inboxPageSetMutualConnectionDialogOpen,
    [INBOX_PAGE_SET_JOIN_GROUP_CONSENT_DIALOG_OPEN]: inboxPageSetJoinGroupConsentDialogOpen,
    [INBOX_PAGE_SET_DATA_PRIVACY_DIALOG_OPEN]: inboxPageSetDataPrivacyDialogOpen,
    [INBOX_PAGE_SET_GROUP_ERROR_DIALOG_OPEN]: inboxPageSetGroupErrorDialogOpen,
    [INBOX_PAGE_SET_DELETE_GROUP_REQ_DIALOG_OPEN]: inboxPageSetDeleteGroupRequestDialogOpen
};

export default function inboxPageReducer(state = INITIAL_STATE, action) {
    const handler = handlers[action.type];

    if (!handler) {
        return state;
    }
    return handler(state, action);
}
